import { GrowthDashboardTopBarActions, LandingPagePreviewView } from '@elva/ui';
import type { LpBlock } from '@elva/ui';
import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useNotify } from '@/data/notifications';
import { blocksToWirePayload } from '../api';
import { useLandingPageDetail, useLandingPageMutations } from '../hooks/useLandingPages';

function livePageUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || trimmed === '—') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function LandingPagePreviewPage() {
  const { landingPageId: routeId } = useParams();
  const [params] = useSearchParams();
  const id = routeId ?? params.get('id') ?? '';
  const navigate = useNavigate();
  const notify = useNotify();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useLandingPageDetail(id);
  const { pause, patchBlocks, refreshTest, declareWinner } = useLandingPageMutations();

  const handleViewLive = useCallback(() => {
    const url = livePageUrl(data?.meta?.url ?? '');
    if (!url) {
      notify.error('Live URL is not available yet');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [data?.meta?.url, notify]);

  const handlePause = useCallback(() => {
    if (!id) return;
    pause.mutate(id, {
      onSuccess: () => {
        void refetch();
      },
    });
  }, [id, pause, refetch]);

  const handleApplyBlocks = useCallback(
    (blocks: LpBlock[]) =>
      new Promise<void>((resolve, reject) => {
        if (!id) {
          reject(new Error('Missing landing page id'));
          return;
        }
        patchBlocks.mutate(
          { id, blocks: blocksToWirePayload(blocks) },
          { onSuccess: () => resolve(), onError: (e) => reject(e) },
        );
      }),
    [id, patchBlocks],
  );

  const handleRefreshTest = useCallback(() => {
    if (!id) return;
    refreshTest.mutate(id, {
      onSuccess: () => {
        void refetch();
      },
    });
  }, [id, refetch, refreshTest]);

  const handleDeclareWinner = useCallback(
    (variantId: string) => {
      if (!id) return;
      declareWinner.mutate(
        { id, variantId },
        {
          onSuccess: () => {
            void refetch();
          },
        },
      );
    },
    [declareWinner, id, refetch],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
    >
      <LandingPagePreviewView
        key={id}
        {...viewProps({
          draft: data ?? undefined,
          loading: isFetching || pause.isPending,
          saving: patchBlocks.isPending,
          refreshingTest: refreshTest.isPending,
          declaringWinner: declareWinner.isPending,
          onBack: () => navigate('/landing-pages'),
          onViewLive: handleViewLive,
          onPause: handlePause,
          onApplyBlocks: handleApplyBlocks,
          onRefreshTest: handleRefreshTest,
          onDeclareWinner: handleDeclareWinner,
        })}
      />
    </AsyncBoundary>
  );
}
